import { useSyncExternalStore } from 'react';
import { setAdMuted } from '@/lib/audio/sfx';

/**
 * Capa de anuncios abstracta. **Desactivada por defecto** (`VITE_ADS_PROVIDER=none`): no se carga
 * ningún SDK ni se muestra nada.
 *
 * Proveedores: `admob` (app Android, con el plugin @capacitor-community/admob), `crazygames` (SDK
 * v3 de CrazyGames), `poki` (SDK v2 de Poki). Todos con la misma interfaz (`AdProvider`).
 *
 * Reglas:
 *  - anuncios SOLO en pausas naturales: cambio de entrada o fin del partido (`naturalBreak`);
 *    nunca durante una jugada (`setInPlay(true)` los bloquea) y como mucho uno cada
 *    `VITE_ADS_MIN_INTERVAL_SEC` (180 s por defecto);
 *  - el audio del juego se silencia mientras dura el anuncio;
 *  - con AdMob hace falta el consentimiento (UMP en la app, `AdConsent` como respaldo); CrazyGames
 *    y Poki gestionan el suyo.
 *
 * Configuración (variables de entorno del build del cliente):
 *   VITE_ADS_PROVIDER=none|admob|crazygames|poki
 *   VITE_ADS_MIN_INTERVAL_SEC=180
 *   VITE_ADMOB_INTERSTITIAL_ID=ca-app-pub-…   (por defecto, el id de PRUEBA de Google)
 *   VITE_ADMOB_TESTING=1                       (anuncios de prueba; 0 en la versión publicada)
 */

export type AdProviderName = 'none' | 'admob' | 'crazygames' | 'poki' | 'test';
export type AdPlacement = 'inning_break' | 'match_end';
export type AdConsentChoice = 'personalized' | 'non_personalized';

export interface AdHooks {
  onStart: () => void;
  onEnd: () => void;
}

export interface AdProvider {
  readonly name: AdProviderName;
  /** Carga e inicia el SDK. */
  init(consent: AdConsentChoice | null): Promise<void>;
  /** Intersticial en una pausa natural. Devuelve si se mostró. */
  showBreak(placement: AdPlacement, hooks: AdHooks, consent: AdConsentChoice | null): Promise<boolean>;
  /** Empieza o termina el juego (CrazyGames y Poki lo piden para sus métricas). */
  gameplayStart?(): void;
  gameplayStop?(): void;
  /** Formulario de consentimiento de la plataforma (UMP de Google en la app). */
  showConsentForm?(): Promise<void>;
}

const env = import.meta.env as unknown as Record<string, string | undefined>;
/** Id de PRUEBA de Google para intersticiales en Android (no genera ingresos). */
const ADMOB_TEST_INTERSTITIAL = 'ca-app-pub-3940256099942544/1033173712';

const loadScript = (src: string): Promise<void> =>
  new Promise((resolve, reject) => {
    if (document.querySelector(`script[src="${src}"]`)) return resolve();
    const s = document.createElement('script');
    s.src = src;
    s.async = true;
    s.onload = () => resolve();
    s.onerror = () => reject(new Error(`No se pudo cargar ${src}`));
    document.head.appendChild(s);
  });

// ─────────────────────────────────────────────
// Proveedores
// ─────────────────────────────────────────────

const noop: AdProvider = {
  name: 'none',
  init: async () => {},
  showBreak: async () => false,
};

/** AdMob (solo en la app Android, con el plugin @capacitor-community/admob instalado). */
type AdMobPlugin = {
  initialize: (o: { initializeForTesting?: boolean }) => Promise<void>;
  requestConsentInfo: () => Promise<{ status: string; isConsentFormAvailable?: boolean }>;
  showConsentForm: () => Promise<{ status: string }>;
  prepareInterstitial: (o: { adId: string; isTesting?: boolean; npa?: boolean }) => Promise<unknown>;
  showInterstitial: () => Promise<void>;
  addListener: (event: string, cb: () => void) => Promise<{ remove: () => void }>;
};
const admobPlugin = (): AdMobPlugin | null =>
  (window as unknown as { Capacitor?: { Plugins?: { AdMob?: AdMobPlugin } } }).Capacitor?.Plugins?.AdMob ?? null;

const admob: AdProvider = {
  name: 'admob',
  async init() {
    const AdMob = admobPlugin();
    if (!AdMob) throw new Error('AdMob no disponible (solo en la app con el plugin @capacitor-community/admob)');
    await AdMob.initialize({ initializeForTesting: env.VITE_ADMOB_TESTING !== '0' });
  },
  async showConsentForm() {
    const AdMob = admobPlugin();
    if (!AdMob) return;
    // UMP (User Messaging Platform) de Google: muestra el formulario GDPR solo si hace falta.
    const info = await AdMob.requestConsentInfo();
    if (info.isConsentFormAvailable && info.status === 'REQUIRED') await AdMob.showConsentForm();
  },
  async showBreak(_placement, hooks, consent) {
    const AdMob = admobPlugin();
    if (!AdMob) return false;
    await AdMob.prepareInterstitial({
      adId: env.VITE_ADMOB_INTERSTITIAL_ID || ADMOB_TEST_INTERSTITIAL,
      isTesting: env.VITE_ADMOB_TESTING !== '0',
      npa: consent !== 'personalized',
    });
    return new Promise<boolean>((resolve) => {
      let done = false;
      const finish = (shown: boolean) => {
        if (done) return;
        done = true;
        hooks.onEnd();
        resolve(shown);
      };
      void AdMob.addListener('interstitialAdDismissed', () => finish(true));
      void AdMob.addListener('interstitialAdFailedToShow', () => finish(false));
      hooks.onStart();
      AdMob.showInterstitial().catch(() => finish(false));
      setTimeout(() => finish(true), 90_000); // red de seguridad
    });
  },
};

/** CrazyGames (SDK v3): https://docs.crazygames.com/ */
type CrazySdk = {
  init: () => Promise<void>;
  ad: { requestAd: (type: 'midgame' | 'rewarded', cb: { adStarted?: () => void; adFinished?: () => void; adError?: (e: unknown) => void }) => void };
  game: { gameplayStart: () => void; gameplayStop: () => void; loadingStop?: () => void };
};
const crazySdk = (): CrazySdk | null => (window as unknown as { CrazyGames?: { SDK?: CrazySdk } }).CrazyGames?.SDK ?? null;

const crazygames: AdProvider = {
  name: 'crazygames',
  async init() {
    await loadScript('https://sdk.crazygames.com/crazygames-sdk-v3.js');
    await crazySdk()?.init();
    crazySdk()?.game.loadingStop?.();
  },
  showBreak: (_placement, hooks) =>
    new Promise<boolean>((resolve) => {
      const sdk = crazySdk();
      if (!sdk) return resolve(false);
      let started = false;
      sdk.ad.requestAd('midgame', {
        adStarted: () => {
          started = true;
          hooks.onStart();
        },
        adFinished: () => {
          hooks.onEnd();
          resolve(true);
        },
        adError: () => {
          if (started) hooks.onEnd();
          resolve(false);
        },
      });
    }),
  gameplayStart: () => crazySdk()?.game.gameplayStart(),
  gameplayStop: () => crazySdk()?.game.gameplayStop(),
};

/** Poki (SDK v2): https://sdk.poki.com/ */
type PokiSdk = {
  init: () => Promise<void>;
  gameLoadingFinished: () => void;
  commercialBreak: (onStart?: () => void) => Promise<void>;
  gameplayStart: () => void;
  gameplayStop: () => void;
};
const pokiSdk = (): PokiSdk | null => (window as unknown as { PokiSDK?: PokiSdk }).PokiSDK ?? null;

const poki: AdProvider = {
  name: 'poki',
  async init() {
    await loadScript('https://game-cdn.poki.com/scripts/v2/poki-sdk.js');
    await pokiSdk()?.init();
    pokiSdk()?.gameLoadingFinished();
  },
  async showBreak(_placement, hooks) {
    const sdk = pokiSdk();
    if (!sdk) return false;
    let started = false;
    await sdk.commercialBreak(() => {
      started = true;
      hooks.onStart();
    });
    if (started) hooks.onEnd();
    return started;
  },
  gameplayStart: () => pokiSdk()?.gameplayStart(),
  gameplayStop: () => pokiSdk()?.gameplayStop(),
};

/** Solo pruebas (`sportsarena.debug=1` + `sportsarena.adsProvider=test`): un «anuncio» de 1,5 s. */
const testProvider: AdProvider = {
  name: 'test',
  init: async () => {},
  async showBreak(placement, hooks) {
    hooks.onStart();
    const el = document.createElement('div');
    el.setAttribute('data-testid', 'test-ad');
    el.style.cssText = 'position:fixed;inset:0;z-index:9999;display:grid;place-items:center;background:#000c;color:#fff;font:600 20px system-ui';
    el.textContent = `Anuncio de prueba (${placement})`;
    document.body.appendChild(el);
    await new Promise((r) => setTimeout(r, 1500));
    el.remove();
    hooks.onEnd();
    return true;
  },
  gameplayStart: () => log.push({ event: 'gameplayStart', at: Date.now() }),
  gameplayStop: () => log.push({ event: 'gameplayStop', at: Date.now() }),
};

// ─────────────────────────────────────────────
// Servicio
// ─────────────────────────────────────────────

const DEBUG = (() => {
  try {
    return typeof window !== 'undefined' && localStorage.getItem('sportsarena.debug') === '1';
  } catch {
    return false;
  }
})();

const pickProvider = (): AdProvider => {
  // Pruebas: solo el proveedor de prueba se puede forzar desde el navegador.
  if (DEBUG) {
    try {
      if (localStorage.getItem('sportsarena.adsProvider') === 'test') return testProvider;
    } catch {
      /* sin almacenamiento */
    }
  }
  const name = (env.VITE_ADS_PROVIDER ?? 'none').trim() as AdProviderName;
  return name === 'admob' ? admob : name === 'crazygames' ? crazygames : name === 'poki' ? poki : noop;
};

const minIntervalMs = (): number => {
  if (DEBUG) {
    try {
      const v = Number(localStorage.getItem('sportsarena.adsMinInterval'));
      if (Number.isFinite(v) && v > 0) return v * 1000;
    } catch {
      /* sin almacenamiento */
    }
  }
  return Math.max(30, Number(env.VITE_ADS_MIN_INTERVAL_SEC ?? 180) || 180) * 1000;
};

const CONSENT_KEY = 'sportsarena.adsConsent';
const log: Array<Record<string, unknown>> = [];
if (DEBUG && typeof window !== 'undefined') (globalThis as unknown as { __sportsarenaAds?: unknown }).__sportsarenaAds = log;

class AdService {
  readonly provider = pickProvider();
  private ready: Promise<boolean> | null = null;
  private inPlay = false;
  private showing = false;
  private lastAdAt = 0;
  private consentListeners = new Set<() => void>();

  /** ¿Hay anuncios configurados? */
  get enabled(): boolean {
    return this.provider.name !== 'none';
  }

  /** ¿Este proveedor necesita nuestra pantalla de consentimiento? (CrazyGames y Poki tienen la suya). */
  get needsConsent(): boolean {
    return this.provider.name === 'admob' || this.provider.name === 'test';
  }

  getConsent(): AdConsentChoice | null {
    try {
      const v = localStorage.getItem(CONSENT_KEY);
      return v === 'personalized' || v === 'non_personalized' ? v : null;
    } catch {
      return null;
    }
  }

  setConsent(choice: AdConsentChoice): void {
    try {
      localStorage.setItem(CONSENT_KEY, choice);
    } catch {
      /* sin almacenamiento */
    }
    this.consentListeners.forEach((l) => l());
    void this.init();
  }

  /** Vuelve a preguntar (Ajustes → «Privacidad de anuncios»). */
  resetConsent(): void {
    try {
      localStorage.removeItem(CONSENT_KEY);
    } catch {
      /* sin almacenamiento */
    }
    this.consentListeners.forEach((l) => l());
  }

  subscribeConsent = (l: () => void): (() => void) => {
    this.consentListeners.add(l);
    return () => this.consentListeners.delete(l);
  };

  /** Inicia el SDK una vez (con anuncios activados y, si hace falta, consentimiento). */
  init(): Promise<boolean> {
    if (!this.enabled) return Promise.resolve(false);
    if (this.needsConsent && !this.getConsent()) return Promise.resolve(false);
    this.ready ??= (async () => {
      try {
        await this.provider.showConsentForm?.();
        await this.provider.init(this.getConsent());
        return true;
      } catch (err) {
        console.warn('[ads] No se pudieron iniciar los anuncios:', (err as Error).message);
        return false;
      }
    })();
    return this.ready;
  }

  /** Durante una jugada no puede salir ningún anuncio. */
  setInPlay(v: boolean): void {
    this.inPlay = v;
  }

  gameplayStart(): void {
    if (this.enabled) this.provider.gameplayStart?.();
  }

  gameplayStop(): void {
    if (this.enabled) this.provider.gameplayStop?.();
  }

  /** Pausa natural (cambio de entrada, fin del partido): quizá un anuncio. Devuelve si salió. */
  async naturalBreak(placement: AdPlacement): Promise<boolean> {
    const now = Date.now();
    const why = !this.enabled
      ? 'off'
      : this.inPlay
        ? 'jugada'
        : this.showing
          ? 'ya hay uno'
          : now - this.lastAdAt < minIntervalMs()
            ? 'intervalo'
            : this.needsConsent && !this.getConsent()
              ? 'sin consentimiento'
              : null;
    if (DEBUG) log.push({ event: 'break', placement, at: now, inPlay: this.inPlay, skipped: why });
    if (why) return false;
    if (!(await this.init())) return false;
    this.showing = true;
    this.lastAdAt = now;
    try {
      const shown = await this.provider.showBreak(
        placement,
        {
          onStart: () => {
            setAdMuted(true);
            if (DEBUG) log.push({ event: 'adStart', placement, at: Date.now(), inPlay: this.inPlay });
          },
          onEnd: () => {
            setAdMuted(false);
            if (DEBUG) log.push({ event: 'adEnd', placement, at: Date.now() });
          },
        },
        this.getConsent()
      );
      return shown;
    } catch (err) {
      setAdMuted(false);
      console.warn('[ads] Error al mostrar el anuncio:', (err as Error).message);
      return false;
    } finally {
      this.showing = false;
    }
  }
}

export const adService = new AdService();
if (DEBUG && typeof window !== 'undefined') (globalThis as unknown as { __sportsarenaAdService?: AdService }).__sportsarenaAdService = adService;

/** Consentimiento actual (para la pantalla y Ajustes). */
export const useAdConsent = (): AdConsentChoice | null =>
  useSyncExternalStore(adService.subscribeConsent, () => adService.getConsent(), () => null);
